import { useState, useEffect } from 'react';
import { Box, Paper, Stepper, Step, StepLabel, Button, Typography, Snackbar, Alert, FormControlLabel, Switch, IconButton, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import InquiryReview from './components/InquiryReview';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getCurrentFYear } from '../../utils/financialYear';

const steps = ['Client Details', 'Product Details', 'Review & Submit'];

// Utility function for deep merging objects
const deepMerge = (target, source) => {
    const output = { ...target };
    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key]);
        } else {
            output[key] = source[key];
        }
    }
    // Explicitly preserve referenceNumber
    if (source.referenceNumber || source.inquiryNumber || source.editreferenceNumber) {
        output.referenceNumber = source.referenceNumber || source.inquiryNumber || source.editreferenceNumber || output.referenceNumber || '';
    }
    return output;
};

const QuotationMaster = ({ connectionString, mode, onPopupCancel }) => {
    const { user, selectedCompany, loading } = useAuth();
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const AppRegID = selectedCompany?.AppRegID || null;
    const fYear = getCurrentFYear();
    const API = connectionString;
    const navigate = useNavigate();
    const { id, inquiryId } = useParams(); // Extract :id and :inquiryId from URL
    console.log(`Mode: ${mode} || inquiryId: ${id} || ${inquiryId}`)
    // State/hooks
    const draftKey = (id) => `inquiryDraft-${userId}-${id || 'new'}`;
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isQuotationLocked, setIsQuotationLocked] = useState(false);

    // Fetch inquiry data for 'inquiry' or 'ref' mode
    useEffect(() => {
        if ((mode !== 'inquiry' && mode !== 'ref') || !inquiryId) return;
        const fetchInquiryWithClient = async (id) => {
            try {
                const res = await axios.get(`${API}/api/inquiry-with-client`, { params: { inquiryId: id, mode: mode } });
                console.log(res.data.data);
                if (res.data?.success) {
                    setFormData((prev) => ({ ...prev, client: res.data.data.client }));
                } else {
                    console.warn("Fetch succeeded but returned no data:", res.data);
                }
            } catch (err) {
                console.error("Fetch error:", err);
                setSnackbar({ open: true, message: "Failed to load inquiry", severity: "error", });
            }
        };
        fetchInquiryWithClient(inquiryId);
    }, [mode, inquiryId]);

    // Fetch quotation data for 'edit' mode or load draft
    const fetchQuotationData = async () => {
        try {
            console.log(`🌐 Fetching quotation data for ID: ${id}`);
            const res = await axios.get(`${API}/api/quot-Order-ref/${id}`);
            console.log('fetchQuotationData Response:', JSON.stringify(res.data, null, 2));
            const data = res.data;
            // Ensure referenceNumber is preserved
            const updatedData = { ...data, client: { ...data.client, referenceNumber: data.client?.referenceNumber || data.client?.inquiryNumber || data.client?.editreferenceNumber || '', }, };
            setFormData(updatedData);
        } catch (error) {
            console.error('Error fetching quotation data:', error);
            setSnackbar({ open: true, message: 'Failed to fetch quotation data.', severity: 'error' });
        }
    };

    useEffect(() => {
        if (mode !== 'edit' || !id) return;

        const checkQuotationUsage = async () => {
            try {
                const res = await axios.get(`${API}/api/check-order-created-from-qoutation/${id}`);
                console.log("res:", res);

                if (res.data?.orderCreated) {
                    setIsQuotationLocked(true);
                }
            } catch (err) {
                console.error('Quotation lock check failed', err);
            }
        };

        checkQuotationUsage();
    }, [mode, id]);

    useEffect(() => {
        const key = draftKey(id);

        const loadDraftData = () => {
            const saved = localStorage.getItem(key);
            if (saved) {
                const data = JSON.parse(saved);
                setFormData(data);
                setActiveStep(steps.length - 1);
            }
        };

        if (mode === 'edit' && id) {
            fetchQuotationData();
        } else {
            loadDraftData();
        }
    }, [mode, id]);

    // Load any saved draft
    useEffect(() => {
        const key = draftKey(formData.id);
        const saved = localStorage.getItem(key);
        if (saved) {
            const data = JSON.parse(saved);
            setFormData(data);
            setActiveStep(steps.length - 1); // Jump to “Review & Submit”
        }
    }, []);

    const handleNext = (stepData = {}) => {
        console.log('Step data:', JSON.stringify(stepData, null, 2));
        const key = activeStep === 0 ? 'client' : 'product';
        setFormData(prev => {
            const newFormData = { ...prev, [key]: stepData[key] || stepData, };
            console.log('Updated formData:', JSON.stringify(newFormData, null, 2));
            return newFormData;
        });
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => {
        console.log(formData.product);
        setActiveStep(prev => prev - 1);
    };

    const handleSubmit = async () => {
        try {
            const payload = { client: formData.client, product: formData.product, companyId, inquiryId, userId, AppRegID, fYear };
            console.log('Final data: ', payload);
            sessionStorage.removeItem("selectedCurrency");
            console.log(mode);
            if (mode === 'add' || mode === 'inquiry') {
                await axios.post(`${API}/api/quote`, payload);
            } else {
                console.log('iD for updating records', id)
                await axios.put(`${API}/api/update-quot/${id}`, payload);
            }
            // localStorage.removeItem(draftKey(formData.id));
            setActiveStep(0);
            setFormData({});
            setSnackbar({ open: true, message: mode === 'add' ? 'Quotation created successfully!' : 'Quotation updated successfully!' });
            setTimeout(() => {
                if (typeof onPopupCancel === "function") onPopupCancel();
                else navigate(-1);
            }, 600);
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Failed to submit Quotation.', severity: 'error' });
        }
    };

    const handleCancel = () => {
        if (typeof onPopupCancel === "function") {
            onPopupCancel();
        } else {
            navigate(-1);
        }
    };

    // ──────────────── Alt+C (Cancel) shortcut — works from any step ───────────
    // Attached here (the wizard's parent), not per-step, so it works regardless
    // of which step (Client Details / Product Details / Review) is active —
    // those step components don't each carry their own cancel concept.
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyC") return;
            e.preventDefault();
            handleCancel();
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    }, [handleCancel]);

    const disablePartyName = formData?.client?.InqId != null;

    const disablePartyNameEdit = Array.isArray(formData?.product?.tableData) && formData.product.tableData.some(row => row?.InqID != null);

    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const renderStepHeader = (icon, title, control = null) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center">
                {activeStep > 0 && (
                    <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight="bold">{icon} {title}</Typography>
            </Box>
            {control}
        </Box>
    );

    const getStepComponent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        {renderStepHeader('1️⃣', 'Client Details')}
                        <ClientDetails connectionString={connectionString} mode={mode} initialData={formData.client || {}} disablePartyName={disablePartyName} disablePartyNameEdit={disablePartyNameEdit} onNext={(data) => handleNext({ client: data })} isQuotationLocked={isQuotationLocked} />
                    </>
                );

            case 1:
                return (
                    <>
                        {renderStepHeader('2️⃣', 'Product Details')}
                        <ProductDetails connectionString={connectionString} initialData={formData.product || {}} clientData={formData.client || {}} onNext={(data) => handleNext({ product: data })} isLocked={isQuotationLocked}
                            onRowsChange={(rows) => {
                                setFormData(prev => ({
                                    ...prev,
                                    product: {
                                        ...(prev.product || {}),
                                        tableData: rows,
                                    }
                                }));
                            }} />
                    </>
                );

            case 2:
                return (
                    <InquiryReview connectionString={connectionString} selectedCompany={selectedCompany} formData={formData} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} mode={mode} isQuotationLocked={isQuotationLocked} />
                );

            default:
                return null;
        }
    };

    return (
        <Box maxWidth="lg" mx="auto" px={2} py={3} pb={0}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
                {/* Back arrow — mobile/tablet only */}
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5, }} >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                    {mode === 'edit' ? 'Edit Quotation' : 'Add New Quotation'}
                </Typography>
            </Box>
            <Paper elevation={4} sx={{ borderRadius: 3, p: 1.5 }}>
                {isQuotationLocked && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Order is already created. Client Details and Product Details can not be modified.
                    </Alert>
                )}
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Divider sx={{ mb: 0.5 }} />
                {getStepComponent(activeStep)}
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default QuotationMaster;